import { BALANCE } from './balance';
import type { Contact } from './security';
import type { PlayerId, Vec3 } from './types';

/**
 * THE GUARD'S HEAD.
 *
 * Pure. It has no geometry, no meshes, no physics and no idea how big the mall
 * is. It is handed a list of thieves it can currently see — the caller does the
 * cone test and the occlusion raycast, because those need the world — and it
 * gives back one point to walk to, one point to look at, and a number saying how
 * sure it is. `src/game/guards.ts` turns that into a capsule that moves.
 *
 * THE ONE IDEA HERE IS THAT BEING SEEN IS A PROCESS, NOT AN EVENT.
 *
 * A guard whose vision cone is a boolean produces a game with no tension in it:
 * either nothing is happening or you have already lost, and there is no moment
 * in between where you get to make a decision. So every thief the guard can see
 * fills an awareness meter, at a rate that falls off with distance and is
 * multiplied by everything the player controls — crouching, standing still,
 * keeping out of the torch beam. Somewhere in the middle of that fill the guard
 * stops walking and turns to face you, which is the game saying "you have about
 * a second and a half". That pause is the entire stealth loop. Take it out and
 * the guards are a lottery.
 *
 * THE GUARD CHASES A MEMORY. Once sight breaks, awareness decays and the guard
 * keeps going to where you were, then sweeps a ring of points around it before
 * giving up. Breaking line of sight is therefore never instantly safe and never
 * permanently unsafe, and the sixteen seconds of sweeping is the window in which
 * you either move somewhere else or get found standing behind the same pillar.
 *
 * NO RANDOMNESS. Not one call to Math.random in this file. The search ring is
 * walked by an integer counter and the patrol route is a list. When the netcode
 * lands, two machines running this brain over the same inputs produce the same
 * guard, and that is only true if it is deterministic.
 */

const G = BALANCE.guard;

export type GuardMode =
  /** Walking the route, bored. */
  | 'patrol'
  /** Stopped, staring at something. Has not committed yet. */
  | 'alerted'
  /** Running at a thief it can see. */
  | 'chase'
  /** Walking to a remembered position and sweeping around it. */
  | 'investigate'
  /** Flat on the floor. */
  | 'downed';

/**
 * The modes, in a fixed order, so a guard's mood is one byte on the wire.
 *
 * Order is load-bearing: it is an index, not a label, and inserting a mode in
 * the middle would silently turn every chasing guard on an older client into an
 * investigating one. New modes go on the end.
 */
export const GUARD_MODES: readonly GuardMode[] = ['patrol', 'alerted', 'chase', 'investigate', 'downed'];

/** Awareness that arrived from the host is filed against this stand-in thief. */
const NET_THIEF = '__net';

/** One thief, as the guard's eyes report them. The caller fills this in. */
export interface Sighting {
  id: PlayerId;
  pos: Vec3;
  /** Metres, eye to eye. */
  dist: number;
  /** Inside the cone AND unoccluded. False means the guard sees nothing. */
  visible: boolean;
  /** Inside the torch beam specifically. Much more conspicuous. */
  lit: boolean;
  crouching: boolean;
  /** Ground speed in metres per second. */
  speed: number;
  /**
   * How brightly the building is lighting this thief, 0..1. Comes from the
   * shop fittings, not from the torch — `lit` is the torch. Defaulted rather
   * than required so a caller that has no light rig (every sim unit test) gets
   * the old behaviour instead of a silent zero, which would read as "the whole
   * map is pitch dark" and quietly make every guard blind.
   */
  exposure?: number;
}

export interface GuardSenses {
  pos: Vec3;
  /** Every active thief, already cone- and occlusion-tested by the caller. */
  sightings: Sighting[];
  /** Camera and noise contacts. Shared across the whole guard team. */
  contacts: Contact[];
  /** The body has arrived at the goal it was last given. */
  atGoal: boolean;
  /** The navigation layer could not route to the last goal. */
  unreachable: boolean;
  /** What to call the place at a point, for the radio. */
  zoneName: (at: Vec3) => string;
}

export interface GuardIntent {
  mode: GuardMode;
  /** Where to walk. Null means stand still. */
  goal: Vec3 | null;
  /** What to face, overriding the direction of travel. Null means look ahead. */
  look: Vec3 | null;
  /** Target ground speed in metres per second. */
  speed: number;
  /** Sweep the head slowly from side to side — the guard is looking, not going. */
  scan: boolean;
  /** A thief close enough to grab, if any. The caller checks the sim's rules. */
  grab: PlayerId | null;
  /** One line of radio traffic, this tick only. Thieves can hear all of it. */
  say: string | null;
}

/**
 * The eight points a guard checks around a last-known position.
 *
 * A ring rather than a random walk: it covers the area, it is deterministic, and
 * when the nav layer says a point is unreachable the brain can simply take the
 * next one instead of having to understand why.
 */
const RING = 8;

export class GuardBrain {
  mode: GuardMode = 'patrol';
  /** Per-thief awareness, 0..1. This is what the HUD's detection bar reads. */
  readonly alert = new Map<PlayerId, number>();
  /** Who the guard is currently interested in. */
  target: PlayerId | null = null;
  /** Where the guard last had eyes on the target, or where a contact landed. */
  lastKnown: Vec3 | null = null;

  private routeIdx = 0;
  /**
   * Which way round the loop. +1 or -1.
   *
   * This is a bigger deal than the start node. A guard walking the ring the other
   * way meets you head-on in a corridor you cleared thirty seconds ago, which is
   * exactly the kind of thing a player cannot memorise their way out of. The
   * routes are hand-authored to read either way round for that reason.
   */
  private dir: 1 | -1 = 1;
  private dwell = 0;
  private searchLeft = 0;
  private ringIdx = 0;
  private ringHops = 0;
  private downFor = 0;
  private goal: Vec3 | null = null;
  /** Contact sources already reacted to, so one camera does not shout twice. */
  private handled = new Set<string>();

  constructor(
    readonly id: PlayerId,
    /** Hand-authored patrol route in world space. May be empty. */
    readonly route: Vec3[],
  ) {}

  /** Knocked down by a thief. Comes back up on its own. */
  knockDown(seconds = BALANCE.capture.guardDownSec): void {
    this.mode = 'downed';
    this.downFor = seconds;
    this.alert.clear();
    this.target = null;
  }

  get isDown(): boolean {
    return this.mode === 'downed';
  }

  /**
   * Wipe everything the guard learned. Used when a new round starts on the same
   * bodies — a guard who remembers last night's chase is a guard who sprints at
   * the loadout bench during the briefing.
   */
  reset(startIdx = 0, dir: 1 | -1 = 1): void {
    this.mode = 'patrol';
    this.alert.clear();
    this.target = null;
    this.lastKnown = null;
    this.routeIdx = this.route.length ? ((startIdx % this.route.length) + this.route.length) % this.route.length : 0;
    this.dir = dir;
    this.dwell = 0;
    this.searchLeft = 0;
    this.ringIdx = 0;
    this.ringHops = 0;
    this.downFor = 0;
    this.goal = null;
    this.handled.clear();
  }

  /** Highest awareness of one thief across everything this guard knows. */
  awarenessOf(thief: PlayerId): number {
    return this.alert.get(thief) ?? 0;
  }

  /**
   * How worked up this guard is about anybody at all.
   *
   * One number rather than a map, because it is what goes on the wire: a client
   * needs the detection bar to fill and the radio to crackle, and neither of
   * those cares which thief the guard is annoyed about.
   */
  get peakAlert(): number {
    let peak = 0;
    for (const v of this.alert.values()) if (v > peak) peak = v;
    return peak;
  }

  /**
   * Accept a mood decided on another machine.
   *
   * A client never runs `think` — the host is the only place a guard decides
   * anything — but the client still has to draw a guard who is lying down and
   * still has to fill the detection bar when the guard is hunting. So the two
   * outputs the rest of the game reads off a brain, the mode and the peak alert,
   * are writable from the wire. Nothing else about the brain is touched: its
   * memory, its route index and its search state stay empty on a client, because
   * they only ever feed `think`, and `think` is not going to run.
   */
  adoptNet(mode: GuardMode, alert: number): void {
    this.mode = mode;
    this.alert.clear();
    if (alert > 0) this.alert.set(NET_THIEF, alert);
  }

  think(dt: number, s: GuardSenses): GuardIntent {
    if (this.mode === 'downed') {
      this.downFor -= dt;
      if (this.downFor > 0) {
        return intent('downed', null, null, 0, false, null, null);
      }
      // Back on your feet facing the way you fell, with no idea what hit you.
      this.mode = 'patrol';
      this.goal = null;
      return intent('patrol', null, null, 0, true, null, 'On my feet. Didn’t see them.');
    }

    let say: string | null = null;

    // --- awareness ---------------------------------------------------------
    // Rise for anything in view, decay for everything else, including thieves
    // that are not in the list at all — a thief who has left the round must not
    // leave a guard permanently half-alert.
    const seen = new Map<PlayerId, Sighting>();
    for (const sight of s.sightings) seen.set(sight.id, sight);
    for (const id of [...this.alert.keys()]) if (!seen.has(id)) this.bleed(id, dt);
    for (const sight of s.sightings) {
      if (sight.visible) this.fill(sight, dt);
      else this.bleed(sight.id, dt);
    }

    // Whoever the guard is most sure about is who it deals with.
    let best: PlayerId | null = null;
    let bestA = 0;
    for (const [id, a] of this.alert) {
      if (a > bestA) {
        bestA = a;
        best = id;
      }
    }

    const sight = best ? seen.get(best) : undefined;
    const visible = !!sight?.visible;
    if (visible && sight) this.lastKnown = { ...sight.pos };

    // --- mode ---------------------------------------------------------------
    const was = this.mode;

    if (bestA >= 1 && visible) {
      this.mode = 'chase';
      this.target = best;
    } else if (this.mode === 'chase') {
      // Hold the chase through a moment behind a pillar; drop it once the
      // memory has faded far enough that the guard genuinely does not know.
      if (bestA < G.chaseHoldAt) {
        this.mode = 'investigate';
        this.beginSearch();
        say = `Lost visual, ${s.zoneName(this.lastKnown ?? s.pos)}. Sweeping.`;
      }
    } else if (visible && bestA >= G.suspectAt) {
      this.mode = 'alerted';
      this.target = best;
    } else if (this.mode === 'alerted') {
      // Whatever it was either resolved into a chase or turned out to be
      // nothing. Go and look where it was rather than shrugging.
      this.mode = 'investigate';
      this.beginSearch();
    }

    // A camera or a noise pulls a bored guard off its route. It does not
    // interrupt a chase — the guard already has something better.
    if (this.mode === 'patrol' || this.mode === 'investigate') {
      const fresh = s.contacts.find((c) => !this.handled.has(key(c)));
      if (fresh) {
        this.handled.add(key(fresh));
        this.target = fresh.thief;
        this.lastKnown = { ...fresh.at };
        const wasPatrolling = this.mode === 'patrol';
        this.mode = 'investigate';
        this.beginSearch();
        say ??= fresh.source.startsWith('cam')
          ? `Camera has someone, ${s.zoneName(fresh.at)}.`
          : `Something moved, ${s.zoneName(fresh.at)}. Checking it.`;
        if (!wasPatrolling) say = `Second contact, ${s.zoneName(fresh.at)}.`;
      }
    }
    // Contacts expire; forget they were handled so the same camera can call the
    // same thief in again ten seconds later.
    if (this.handled.size) {
      const live = new Set(s.contacts.map(key));
      for (const k of this.handled) if (!live.has(k)) this.handled.delete(k);
    }

    if (this.mode !== was) {
      if (this.mode === 'chase') {
        say = `Contact — ${s.zoneName(sight?.pos ?? s.pos)}. I’m on them.`;
      } else if (this.mode === 'alerted' && was === 'patrol') {
        say ??= `Hold on. Movement, ${s.zoneName(sight?.pos ?? s.pos)}.`;
      }
    }

    // --- behaviour ----------------------------------------------------------
    switch (this.mode) {
      case 'chase': {
        const at = sight?.pos ?? this.lastKnown ?? s.pos;
        this.goal = { ...at };
        const grab = sight && sight.dist <= G.grabM ? sight.id : null;
        return intent('chase', this.goal, at, G.chaseSpeed, false, grab, say);
      }

      case 'alerted': {
        // The tell. Stop dead, face it, and let the player read the room.
        return intent('alerted', null, sight?.pos ?? this.lastKnown, 0, false, null, say);
      }

      case 'investigate': {
        this.searchLeft -= dt;
        if (this.searchLeft <= 0) {
          this.mode = 'patrol';
          this.target = null;
          this.goal = null;
          return intent('patrol', this.patrolGoal(s), null, G.patrolSpeed, false, null, 'Nothing here. Back on route.');
        }
        if (!this.lastKnown) {
          this.mode = 'patrol';
          return intent('patrol', this.patrolGoal(s), null, G.patrolSpeed, false, null, say);
        }
        // Walk to the point, look around, then take the next point on the ring.
        // Unreachable ones are skipped rather than diagnosed: the ring is a
        // guess about where somebody went, and a guess that lands inside a wall
        // is not worth a special case.
        if (s.unreachable || (s.atGoal && this.dwell <= 0)) {
          this.nextRingPoint();
        }
        if (s.atGoal) {
          this.dwell -= dt;
          if (this.dwell <= 0) this.nextRingPoint();
          return intent('investigate', null, null, 0, true, null, say);
        }
        this.dwell = 1.6;
        return intent('investigate', this.goal, null, G.investigateSpeed, false, null, say);
      }

      default: {
        // Patrol. Walk to the waypoint, stand and scan, move on.
        if (!this.route.length) return intent('patrol', null, null, 0, true, null, say);
        if (s.unreachable) {
          this.routeIdx = this.step();
          this.goal = { ...this.route[this.routeIdx] };
          this.dwell = 0;
          return intent('patrol', this.goal, null, G.patrolSpeed, false, null, say);
        }
        if (s.atGoal && this.goal) {
          this.dwell -= dt;
          if (this.dwell > 0) return intent('patrol', null, null, 0, true, null, say);
          this.routeIdx = this.step();
          this.goal = { ...this.route[this.routeIdx] };
          this.dwell = G.dwellSec;
          return intent('patrol', this.goal, null, G.patrolSpeed, false, null, say);
        }
        this.goal ??= this.patrolGoal(s);
        this.dwell = G.dwellSec;
        return intent('patrol', this.goal, null, G.patrolSpeed, false, null, say);
      }
    }
  }

  // -------------------------------------------------------------------------

  /**
   * How fast this thief is being noticed, per second.
   *
   * Every term is something the player is doing. Distance is the only one they
   * do not fully control, and even that is a choice about which way to run.
   */
  private fill(sight: Sighting, dt: number): void {
    const near = clamp01(1 - sight.dist / G.viewRangeM);
    let rate = G.noticeRate * (0.25 + 0.75 * near * near);
    if (sight.lit) rate *= G.beamMul;
    else rate *= shadow(sight.exposure);
    if (sight.crouching) rate *= G.crouchMul;
    if (sight.speed > BALANCE.move.walkSpeed * 1.15) rate *= G.sprintMul;
    else if (sight.speed < 0.35) rate *= G.stillMul;
    // Close enough to touch: crouching in the dark stops helping.
    if (sight.dist < G.pointBlankM) rate = Math.max(rate, G.noticeRate);
    this.alert.set(sight.id, clamp01((this.alert.get(sight.id) ?? 0) + rate * dt));
  }

  private bleed(id: PlayerId, dt: number): void {
    const next = (this.alert.get(id) ?? 0) - G.forgetRate * dt;
    if (next <= 0) this.alert.delete(id);
    else this.alert.set(id, next);
  }

  private beginSearch(): void {
    this.searchLeft = G.searchSec;
    this.ringIdx = 0;
    this.ringHops = 0;
    this.dwell = 0;
    // First stop is the spot itself. The ring only starts once that is checked.
    this.goal = this.lastKnown ? { ...this.lastKnown } : null;
  }

  private nextRingPoint(): void {
    if (!this.lastKnown) return;
    // Spiral outward a little each lap so a long search widens rather than
    // pacing the same eight flagstones for sixteen seconds.
    const lap = Math.floor(this.ringHops / RING);
    const r = G.searchRadiusM * (0.55 + 0.45 * Math.min(2, lap));
    const a = (this.ringIdx / RING) * Math.PI * 2 + lap * 0.4;
    this.ringIdx = (this.ringIdx + 3) % RING;
    this.ringHops++;
    this.goal = {
      x: this.lastKnown.x + Math.cos(a) * r,
      y: this.lastKnown.y,
      z: this.lastKnown.z + Math.sin(a) * r,
    };
    this.dwell = 1.4;
  }

  /** One node along the loop, in whichever direction this guard is walking it. */
  private step(): number {
    const n = this.route.length;
    return (this.routeIdx + this.dir + n) % n;
  }

  /** Nearest point on the route, so a guard coming back does not walk the lap. */
  private patrolGoal(s: GuardSenses): Vec3 | null {
    if (!this.route.length) return null;
    let bestI = 0;
    let bestD = Infinity;
    for (let i = 0; i < this.route.length; i++) {
      const d = flat(this.route[i], s.pos);
      if (d < bestD) {
        bestD = d;
        bestI = i;
      }
    }
    this.routeIdx = bestI;
    this.goal = { ...this.route[bestI] };
    return this.goal;
  }
}

function intent(
  mode: GuardMode,
  goal: Vec3 | null,
  look: Vec3 | null,
  speed: number,
  scan: boolean,
  grab: PlayerId | null,
  say: string | null,
): GuardIntent {
  return { mode, goal, look, speed, scan, grab, say };
}

/** A contact is the same contact while it is the same thief from the same eye. */
function key(c: Contact): string {
  return `${c.thief}@${c.source}`;
}

function flat(a: Vec3, b: Vec3): number {
  return Math.hypot(a.x - b.x, a.z - b.z) + Math.abs(a.y - b.y) * 4;
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/**
 * The concealment curve: exposure in, notice-rate multiplier out.
 *
 * Linear on purpose. The player has to be able to look at a floor, guess how
 * dark it is, and be right — a curve with a knee in it would mean the useful
 * half of the range lived somewhere they could not see. Exported because the
 * HUD draws this exact number and the two must not drift apart.
 */
export function shadow(exposure: number | undefined): number {
  if (exposure === undefined) return 1;
  const t = clamp01(exposure / BALANCE.guard.fullyLitAt);
  return BALANCE.guard.shadowMul + (1 - BALANCE.guard.shadowMul) * t;
}
